import React, { forwardRef } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';
import { TextReg } from '../StyledText';

type TableCaptionProps = ViewProps & {
  children: React.ReactNode;
};

/**
 * TableCaption - Table caption/description
 *
 * Use to provide a title or description for the table.
 * Typically placed before TableHeader or after TableBody.
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableCaption>A list of all users in the system</TableCaption>
 *   <TableHeader>...</TableHeader>
 *   <TableBody>...</TableBody>
 * </Table>
 * ```
 */
export const TableCaption = forwardRef<View, TableCaptionProps>(
  ({ children, style, ...props }, ref) => {
    const styles = useDynamicStyles(createStyles);

    return (
      <View ref={ref} style={[styles.caption, style]} {...props}>
        {typeof children === 'string' ? (
          <TextReg style={styles.captionText}>{children}</TextReg>
        ) : (
          children
        )}
      </View>
    );
  },
);

TableCaption.displayName = 'TableCaption';

const createStyles = () =>
  StyleSheet.create({
    caption: {
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    captionText: {
      fontSize: 14,
      color: Colors.opacity50,
      textAlign: 'center',
    },
  });
